import { useMutation } from '@apollo/client';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText
} from '@mui/material';
import {
  advanceActionLabel,
  GroupTrial,
  nextGroupTrialStatus,
  statusLabel
} from 'src/models/groupTrial';
import {
  ADMIN_RETRIEVE_GROUP_TRIALS,
  UPDATE_GROUP_TRIAL_STATUS
} from './operations';

type Props = {
  open: boolean;
  organizationId: string;
  trial: GroupTrial;
  onClose: () => void;
  onAdvanced: () => void;
};

// Simple confirm dialog for advancing a trial one step along the forward-only
// lifecycle. Deliberately NOT the typed ConfirmModal (which requires typing a
// confirmation string) — a single Confirm button is the intended interaction.
const AdvanceGroupTrialStatusDialog = ({
  open,
  organizationId,
  trial,
  onClose,
  onAdvanced
}: Props) => {
  const next = nextGroupTrialStatus(trial.status);

  const [executeAdvance, { loading, error }] = useMutation(
    UPDATE_GROUP_TRIAL_STATUS,
    {
      errorPolicy: 'all',
      refetchQueries: [
        { query: ADMIN_RETRIEVE_GROUP_TRIALS, variables: { organizationId } }
      ]
    }
  );

  const handleConfirm = async () => {
    if (!next) {
      return;
    }
    const result = await executeAdvance({
      variables: { input: { id: trial.id, status: next } }
    });
    // errorPolicy 'all' resolves on GraphQL errors; only close on real data.
    if (result.data?.UpdateGroupTrialStatus) {
      onAdvanced();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{advanceActionLabel(trial.status)}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {next
            ? `Move "${trial.name}" from ${statusLabel(
                trial.status
              )} to ${statusLabel(next)}? This cannot be undone.`
            : `"${trial.name}" is already ${statusLabel(
                trial.status
              )} and cannot be advanced further.`}
        </DialogContentText>
        {error && <FormHelperText error>{error.message}</FormHelperText>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || !next}
        >
          {loading ? 'Advancing…' : advanceActionLabel(trial.status)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvanceGroupTrialStatusDialog;
